import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { LoadingSpinner, DataTable, columnTypes, number, plusMinus } from 'yankees-react-utils';
import { useOktaQuery, Advance, Team } from '../../../data';
import ReportCard from '../../../components/ReportCard';

const StyledOppLeads = styled.div`
    .oppleads_highlight {
        background-color: #33dde5 !important;
    }
    .oppleads_selected {
        background-color: #ffff00 !important;
    }
    .oppleads_plus {
        background-color: #00ff21 !important;
    }
`;

const leagueSummaryCols = (translate) => [
    { id: 'idx', title: '#', transform: (_row, idx) => idx + 1 },
    {
        id: 'name',
        title: 'Team',
        type: columnTypes.TEXT,
        transform: ({ mlbamteamname }) => (mlbamteamname === 'Level Avg' ? `MLB ${translate('common.abbr-average')}` : mlbamteamname),
    },
    { id: 'avg1bprimheldon', title: <>{translate('baseball.1b')} {translate('sections.opponent.team.primary')}<br />({translate('sections.opponent.team.held-on')})</>, precision: 2 },
    {
        id: 'pm1bprimheldon',
        title: <>{translate('baseball.1b')} {translate('sections.opponent.team.primary')} {translate('sections.opponent.team.above-below')}<br />{translate('sections.opponent.team.expected')} ({translate('sections.opponent.team.held-on')})</>,
        type: columnTypes.TEXT,
        transform: ({ pm1bprimheldon }) => plusMinus(number(pm1bprimheldon, 2)),
    },
    { id: 'avg1bsecheldon', title: <>{translate('baseball.1b')} {translate('sections.opponent.team.secondary')}<br />({translate('sections.opponent.team.held-on')})</>, precision: 2 },
    {
        id: 'pm1bsecheldon',
        title: <>{translate('baseball.1b')} {translate('sections.opponent.team.secondary')} {translate('sections.opponent.team.above-below')}<br />{translate('sections.opponent.team.expected')} ({translate('sections.opponent.team.held-on')})</>,
        type: columnTypes.TEXT,
        transform: ({ pm1bsecheldon }) => plusMinus(number(pm1bsecheldon, 2)),
    },
    { id: 'avg1bprimnotheldon', title: <>{translate('baseball.1b')} {translate('sections.opponent.team.primary')}<br />({translate('sections.opponent.team.not-held-on')})</>, precision: 2 },
    { id: 'avg1bsecnotheldon', title: <>{translate('baseball.1b')} {translate('sections.opponent.team.secondary')}<br />({translate('sections.opponent.team.not-held-on')})</>, precision: 2 },
    { id: 'avg2bsec', title: `${translate('baseball.2b')} ${translate('sections.opponent.team.secondary')}`, precision: 2 },
    { id: 'avg3bprim', title: `${translate('baseball.3b')} ${translate('sections.opponent.team.primary')}`, precision: 2 },
    { id: 'avg3bsec', title: `${translate('baseball.3b')} ${translate('sections.opponent.team.secondary')}`, precision: 2 },
    { id: 'cpko', title: translate('sections.opponent.team.abbr-cpko') },
    { id: 'pcs', title: translate('sections.opponent.team.abbr-pcs') },
    { id: 'ppko', title: translate('sections.opponent.team.abbr-ppko') },
    { id: 'totpko', title: translate('sections.opponent.team.abbr-total-pko') },
];

const firstBaseLeadsColumns = (translate) => [
    { id: 'idx', title: '#', transform: (_row, idx) => idx + 1 },
    { id: 'name', title: translate('sections.opponent.team.name'), type: columnTypes.TEXT },
    { id: 'hto1b', title: <>{translate('sections.opponent.team.home-to-1b')}<br />{translate('sections.opponent.team.time')}</>, precision: 2 },
    { id: 'n', title: `# ${translate('sections.opponent.team.leads')}` },
    { id: 'avg1bprimheldon', title: <>{translate('sections.opponent.team.primary')}<br />({translate('sections.opponent.team.held-on')})</>, precision: 2 },
    {
        id: 'pm1bprim',
        title: <>{translate('sections.opponent.team.primary')} {translate('sections.opponent.team.above-below')}<br />{translate('sections.opponent.team.expected')} ({translate('sections.opponent.team.held-on')})</>,
        type: columnTypes.TEXT,
        transform: ({ pm1bprim }) => plusMinus(number(pm1bprim, 2)),
        cellClass: ({ pm1bprim }) => (pm1bprim >= 1 ? 'oppleads_plus' : null),
    },
    { id: 'avg1bsecheldon', title: <>{translate('sections.opponent.team.secondary')}<br />({translate('sections.opponent.team.held-on')})</>, precision: 2 },
    {
        id: 'pm1bsec',
        title: <>{translate('sections.opponent.team.secondary')} {translate('sections.opponent.team.above-below')}<br />{translate('sections.opponent.team.expected')} ({translate('sections.opponent.team.held-on')})</>,
        type: columnTypes.TEXT,
        transform: ({ pm1bsec }) => plusMinus(number(pm1bsec, 2)),
        cellClass: ({ pm1bsec }) => (pm1bsec >= 1 ? 'oppleads_plus' : null),
    },
    { id: 'avg1bprimnotheldon', title: <>{translate('sections.opponent.team.primary')}<br />({translate('sections.opponent.team.not-held-on')})</>, precision: 2 },
    { id: 'avg1bsecnotheldon', title: <>{translate('sections.opponent.team.secondary')}<br />({translate('sections.opponent.team.not-held-on')})</>, precision: 2 },
    { id: 'cpko', title: translate('sections.opponent.team.abbr-cpko') },
    { id: 'pcs', title: translate('sections.opponent.team.abbr-pcs') },
    { id: 'ppko', title: translate('sections.opponent.team.abbr-ppko') },
    { id: 'totpko', title: translate('sections.opponent.team.abbr-total-pko') },
];

const secondBaseLeadsColumns = (translate) => [
    { id: 'idx', title: '#', transform: (_row, idx) => idx + 1 },
    { id: 'name', title: translate('sections.opponent.team.name'), type: columnTypes.TEXT },
    { id: 'hto1b', title: <>{translate('sections.opponent.team.home-to-1b')}<br />{translate('sections.opponent.team.time')}</>, precision: 2 },
    { id: 'n2b', title: `# ${translate('sections.opponent.team.leads')}` },
    { id: 'avg2bprim', title: translate('sections.opponent.team.primary'), precision: 2 },
    {
        id: 'pm2bprim',
        title: <>{translate('sections.opponent.team.primary')} {translate('sections.opponent.team.above-below')}<br />{translate('sections.opponent.team.expected')}</>,
        type: columnTypes.TEXT,
        transform: ({ pm2bprim }) => plusMinus(number(pm2bprim, 2)),
        cellClass: ({ pm2bprim }) => (pm2bprim >= 1 ? 'oppleads_plus' : null),
    },
    { id: 'avg2bsec', title: translate('sections.opponent.team.secondary'), precision: 2 },
    {
        id: 'pm2bsec',
        title: <>{translate('sections.opponent.team.secondary')} {translate('sections.opponent.team.above-below')}<br />{translate('sections.opponent.team.expected')}</>,
        type: columnTypes.TEXT,
        transform: ({ pm2bsec }) => plusMinus(number(pm2bsec, 2)),
        cellClass: ({ pm2bsec }) => (pm2bsec >= 1 ? 'oppleads_plus' : null),
    },
    { id: 'cpko', title: translate('sections.opponent.team.abbr-cpko') },
    { id: 'pcs', title: translate('sections.opponent.team.abbr-pcs') },
    { id: 'ppko', title: translate('sections.opponent.team.abbr-ppko') },
    { id: 'totpko', title: translate('sections.opponent.team.abbr-total-pko') },
];
const thirdBaseLeadsColumns = (translate) => [
    { id: 'idx', title: '#', transform: (_row, idx) => idx + 1 },
    { id: 'name', title: translate('sections.opponent.team.name'), type: columnTypes.TEXT },
    { id: 'hto1b', title: <>{translate('sections.opponent.team.home-to-1b')}<br />{translate('sections.opponent.team.time')}</>, precision: 2 },
    { id: 'n3b', title: `# ${translate('sections.opponent.team.leads')}` },
    { id: 'avg3bprim', title: translate('sections.opponent.team.primary'), precision: 2 },
    {
        id: 'pm3bprim',
        title: <>{translate('sections.opponent.team.primary')} {translate('sections.opponent.team.above-below')}<br />{translate('sections.opponent.team.expected')}</>,
        type: columnTypes.TEXT,
        transform: ({ pm3bprim }) => plusMinus(number(pm3bprim, 2)),
        cellClass: ({ pm3bprim }) => (pm3bprim >= 1 ? 'oppleads_plus' : null),
    },
    { id: 'avg3bsec', title: translate('sections.opponent.team.secondary'), precision: 2 },
    {
        id: 'pm3bsec',
        title: <>{translate('sections.opponent.team.secondary')} {translate('sections.opponent.team.above-below')}<br />{translate('sections.opponent.team.expected')}</>,
        type: columnTypes.TEXT,
        transform: ({ pm3bsec }) => plusMinus(number(pm3bsec, 2)),
        cellClass: ({ pm3bsec }) => (pm3bsec >= 1 ? 'oppleads_plus' : null),
    },
    { id: 'cpko', title: translate('sections.opponent.team.abbr-cpko') },
    { id: 'pcs', title: translate('sections.opponent.team.abbr-pcs') },
    { id: 'ppko', title: translate('sections.opponent.team.abbr-ppko') },
    { id: 'totpko', title: translate('sections.opponent.team.abbr-total-pko') },
];

const defaultOppLeadsParams = { yearId: -1, levelId: 8 };
const OpponentLeadsReport = ({ match: { params } }) => {
    const { t: translate } = useTranslation();
    const oppLeadsParams = { ...defaultOppLeadsParams, teamId: params.teamId };
    const { data, isLoading } = useOktaQuery(['oppLeads', oppLeadsParams], (t) => Advance.oppleads(t, oppLeadsParams));
    const { data: team } = useOktaQuery(['team', params.teamId], (t) => Team.get(t, params.teamId));

    const leagueSummaryRowClass = ({ mlbamteamname, teamid }) => {
        const cssClasses = [];
        if (mlbamteamname === 'Level Avg') cssClasses.push('oppleads_highlight');
        if (teamid?.toString() === params.teamId.toString()) cssClasses.push('oppleads_selected');
        return cssClasses.length ? cssClasses.join(' ') : null;
    };

    return (
        <StyledOppLeads>
            {isLoading && <LoadingSpinner />}
            {data?.teamtotals && (
                <ReportCard title={translate('sections.opponent.team.league-summary')}>
                    <DataTable
                        rows={data.teamtotals}
                        columns={leagueSummaryCols(translate)}
                        rowClass={leagueSummaryRowClass}
                        sortable={{ id: 'avg1bprimheldon', isDesc: true }}
                    />
                </ReportCard>
            )}
            {data?.players1brhp && (
                <ReportCard title={`${team?.FullName} - ${translate('baseball.1b')} ${translate('sections.opponent.team.leads')} vs RHP`}>
                    <DataTable rows={data.players1brhp} columns={firstBaseLeadsColumns(translate)} sortable={{ id: 'avg1bprimheldon', isDesc: true }} />
                </ReportCard>
            )}
            {data?.players1blhp && (
                <ReportCard title={`${team?.FullName} - ${translate('baseball.1b')} ${translate('sections.opponent.team.leads')} vs LHP`}>
                    <DataTable rows={data.players1blhp} columns={firstBaseLeadsColumns(translate)} sortable={{ id: 'avg1bprimheldon', isDesc: true }} />
                </ReportCard>
            )}
            {data?.players2b && (
                <ReportCard title={`${team?.FullName} - ${translate('baseball.2b')} ${translate('sections.opponent.team.leads')}`}>
                    <DataTable rows={data.players2b} columns={secondBaseLeadsColumns(translate)} sortable={{ id: 'avg2bprim', isDesc: true }} />
                </ReportCard>
            )}
            {data?.players3b && (
                <ReportCard title={`${team?.FullName} - ${translate('baseball.3b')} ${translate('sections.opponent.team.leads')}`}>
                    <DataTable rows={data.players3b} columns={thirdBaseLeadsColumns(translate)} sortable={{ id: 'avg3bprim', isDesc: true }} />
                </ReportCard>
            )}
        </StyledOppLeads>
    );
};

OpponentLeadsReport.propTypes = {
    match: PropTypes.shape({
        params: PropTypes.shape({
            teamId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        }).isRequired,
    }).isRequired,
};

export default OpponentLeadsReport;
